/**
 * Conference intake (CONFERENCE-INTAKE) — consent + survey forms, the
 * tenant's intake settings, the booth flow (badge photo → details → consent →
 * survey → participant) and each participant's intake record.
 *
 * Forms are versioned: editing saves a new version under the same family, and
 * every response points at the exact version the participant saw.
 */
import { apiClient } from './client';

export type IntakeFormKind = 'consent' | 'survey' | 'or_survey' | 'sim_survey';
/** POST-SURVEYS: the post-survey kinds. OR = shown by the capture app after a
 *  recording; sim = answered on the participant's phone from the station QR. */
export type PostSurveyKind = 'or_survey' | 'sim_survey';
export const isPostSurvey = (k: IntakeFormKind): k is PostSurveyKind =>
  k === 'or_survey' || k === 'sim_survey';
/** What the capture app can show after a recording (server: OR_SURVEY_TYPES). */
export const OR_SURVEY_TYPES: readonly QuestionType[] = [
  'heading', 'text', 'long_text', 'number', 'single', 'multi', 'yesno',
];

export type QuestionType =
  | 'heading'
  | 'text'
  | 'long_text'
  | 'email'
  | 'number'
  | 'percent'
  | 'single'
  | 'multi'
  | 'yesno'
  | 'date'
  | 'month_year'
  | 'percent_split';

export interface SurveyQuestion {
  /** Stable key for the answer — letters, digits, _ and -. */
  id: string;
  type: QuestionType;
  label: string;
  help?: string;
  required?: boolean;
  /** single / multi */
  options?: string[];
  /** single / multi: also accept a typed "Other" answer. */
  allow_other?: boolean;
  /** percent_split: the parts that should add up to 100. */
  parts?: string[];
  /** number: bounds. */
  min?: number;
  max?: number;
  /** Only shown when question `id` has the answer `equals`. */
  show_if?: { id: string; equals: string };
}

export interface SurveyDefinition {
  title: string;
  subtitle?: string;
  intro?: string;
  irb_approval?: string;
  irb_expiration?: string;
  questions: SurveyQuestion[];
}

export interface ConsentSection {
  heading?: string;
  /** Plain text; blank lines separate paragraphs, "• " starts a bullet. */
  body: string;
}

export interface ConsentChoice {
  id: string;
  label: string;
  /** Usually ["Yes", "No"]. */
  options: string[];
  /** The participant initials next to their choice. */
  initials?: boolean;
}

export interface ConsentDefinition {
  title: string;
  institution?: string;
  protocol_director?: string;
  protocol_title?: string;
  irb_approval?: string;
  irb_expiration?: string;
  sections: ConsentSection[];
  choices: ConsentChoice[];
  copy_note?: string;
  signature_label: string;
  printed_name_label: string;
}

export interface IntakeForm<D = SurveyDefinition | ConsentDefinition> {
  /** This version's id — what a response points at. */
  id: string;
  /** Shared by every version of the form. */
  family_id: string;
  version: number;
  kind: IntakeFormKind;
  name: string;
  definition: D;
  created_at: string;
  /** A copy of another form; only copies can be deleted (originals are
   *  archived). Absent from older responses. */
  is_copy?: boolean;
}

export interface IntakeTemplate {
  key: string;
  kind: IntakeFormKind;
  name: string;
}

export interface IntakeConfig {
  enabled: boolean;
  consent_family_id: string | null;
  survey_family_id: string | null;
  /** Latest version of the chosen consent / survey (null when none). */
  consent_form: IntakeForm<ConsentDefinition> | null;
  survey_form: IntakeForm<SurveyDefinition> | null;
}

/** What the vision model read off a badge photo (null = not printed). */
export interface BadgeRead {
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  organization: string | null;
  city: string | null;
  state_region: string | null;
  country: string | null;
  badge_role: string | null;
  conference_name: string | null;
  attendee_id: string | null;
}

/** Answer shapes by question type: text/email/single/yesno/date/month_year →
 *  string; number/percent → number; multi → string[]; percent_split →
 *  { part: number }. */
export type SurveyAnswers = Record<string, unknown>;

export interface IntakeSubmission {
  participant: {
    full_name: string;
    email?: string | null;
    title?: string | null;
    years_of_experience?: number | null;
    city?: string | null;
    state_region?: string | null;
    country?: string | null;
    /** US National Provider Identifier, 10 digits (optional). */
    npi?: string | null;
  };
  conference_id?: string | null;
  organization?: string | null;
  /** data:image/jpeg;base64,… (keep it under ~3 MB — downscale first). */
  badge_photo?: string | null;
  badge_read?: BadgeRead | null;
  consent?: {
    form_id: string;
    choices: Record<string, string>;
    initials?: string | null;
    signed_name: string;
    /** data:image/png;base64,… of the drawn signature. */
    signature: string;
  } | null;
  survey?: { form_id: string; answers: SurveyAnswers } | null;
}

export interface ParticipantIntake {
  id: string;
  participant_user_id: string;
  conference_id: string | null;
  organization: string | null;
  badge_read: Partial<BadgeRead> | null;
  has_badge_photo: boolean;
  consent_form: IntakeForm<ConsentDefinition> | null;
  consent_answers: { choices: Record<string, string>; initials: string | null } | null;
  signed_name: string | null;
  has_signature: boolean;
  consented_at: string | null;
  survey_form: IntakeForm<SurveyDefinition> | null;
  survey_answers: SurveyAnswers | null;
  collected_by_user_id: string | null;
  created_at: string;
}

const base = (tenantId: string) => `/api/v1/tenants/${tenantId}`;

export async function listIntakeTemplates(tenantId: string): Promise<IntakeTemplate[]> {
  const { data } = await apiClient.get<IntakeTemplate[]>(`${base(tenantId)}/intake/templates`);
  return data;
}

export async function listIntakeForms(tenantId: string): Promise<IntakeForm[]> {
  const { data } = await apiClient.get<IntakeForm[]>(`${base(tenantId)}/intake/forms`);
  return data;
}

export async function createIntakeFormFromTemplate(
  tenantId: string,
  template: string,
): Promise<IntakeForm> {
  const { data } = await apiClient.post<IntakeForm>(`${base(tenantId)}/intake/forms`, { template });
  return data;
}

export async function createIntakeForm(
  tenantId: string,
  kind: IntakeFormKind,
  name: string,
  definition: SurveyDefinition | ConsentDefinition,
): Promise<IntakeForm> {
  const { data } = await apiClient.post<IntakeForm>(`${base(tenantId)}/intake/forms`, {
    kind,
    name,
    definition,
  });
  return data;
}

export interface IntakeCopySource {
  tenant_id: string;
  tenant_name: string;
  forms: { family_id: string; version: number; kind: IntakeFormKind; name: string }[];
}

/** Forms in other organisations the caller may copy into this one. */
export async function listIntakeCopySources(tenantId: string): Promise<IntakeCopySource[]> {
  const { data } = await apiClient.get<IntakeCopySource[]>(
    `${base(tenantId)}/intake/copy-sources`,
  );
  return data;
}

/** Copy another organisation's form (its latest version) into this one. */
export async function copyIntakeFormFrom(
  tenantId: string,
  sourceTenantId: string,
  familyId: string,
): Promise<IntakeForm> {
  const { data } = await apiClient.post<IntakeForm>(`${base(tenantId)}/intake/forms`, {
    copy_from: { tenant_id: sourceTenantId, family_id: familyId },
  });
  return data;
}

/** Saves a NEW version; earlier responses keep the version they answered. */
export async function saveIntakeForm(
  tenantId: string,
  familyId: string,
  name: string,
  definition: SurveyDefinition | ConsentDefinition,
  /** Post surveys only: re-flag OR <-> sim with this version. */
  kind?: PostSurveyKind,
): Promise<IntakeForm> {
  const { data } = await apiClient.put<IntakeForm>(
    `${base(tenantId)}/intake/forms/${familyId}`,
    kind ? { name, definition, kind } : { name, definition },
  );
  return data;
}

export interface PostSurveyConfig {
  or_family_id: string | null;
  sim_family_id: string | null;
  or_form: IntakeForm<SurveyDefinition> | null;
  sim_form: IntakeForm<SurveyDefinition> | null;
  /** The sim station's code (page /post-survey/<code>), or null. */
  station_code: string | null;
}

export async function getPostSurveyConfig(tenantId: string): Promise<PostSurveyConfig> {
  const { data } = await apiClient.get<PostSurveyConfig>(`${base(tenantId)}/post-surveys/config`);
  return data;
}

export async function savePostSurveyConfig(
  tenantId: string,
  body: { or_family_id: string | null; sim_family_id: string | null },
): Promise<PostSurveyConfig> {
  const { data } = await apiClient.put<PostSurveyConfig>(
    `${base(tenantId)}/post-surveys/config`,
    body,
  );
  return data;
}

/** Create the sim station code, or replace it (the old poster stops working). */
export async function newStationCode(tenantId: string): Promise<PostSurveyConfig> {
  const { data } = await apiClient.post<PostSurveyConfig>(
    `${base(tenantId)}/post-surveys/station-code`,
  );
  return data;
}

export async function removeStationCode(tenantId: string): Promise<PostSurveyConfig> {
  const { data } = await apiClient.delete<PostSurveyConfig>(
    `${base(tenantId)}/post-surveys/station-code`,
  );
  return data;
}

/** The public page a station code opens. */
export function stationUrl(code: string): string {
  return `${window.location.origin}/post-survey/${code}`;
}

/** Delete a copied form for good. The server refuses an original, a form
 *  intake uses, and one with responses (archive that instead). */
export async function deleteIntakeFormCopy(tenantId: string, familyId: string): Promise<void> {
  await apiClient.delete(`${base(tenantId)}/intake/forms/${familyId}/permanent`);
}

export async function archiveIntakeForm(tenantId: string, familyId: string): Promise<void> {
  await apiClient.delete(`${base(tenantId)}/intake/forms/${familyId}`);
}

export async function getIntakeConfig(tenantId: string): Promise<IntakeConfig> {
  const { data } = await apiClient.get<IntakeConfig>(`${base(tenantId)}/intake/config`);
  return data;
}

export async function saveIntakeConfig(
  tenantId: string,
  config: { enabled: boolean; consent_family_id: string | null; survey_family_id: string | null },
): Promise<IntakeConfig> {
  const { data } = await apiClient.put<IntakeConfig>(`${base(tenantId)}/intake/config`, config);
  return data;
}

/** Read name / organisation / city off a badge photo (data URL). 503 when
 *  badge reading isn't set up; 502 when it couldn't read it — both mean
 *  "type it in". */
export async function readBadge(tenantId: string, image: string): Promise<BadgeRead> {
  const { data } = await apiClient.post<BadgeRead>(`${base(tenantId)}/intake/badge-read`, {
    image,
  });
  return data;
}

/** Enroll a participant from the booth. 409 with
 *  `detail.participant_user_id` when the badge is already enrolled. */
export async function submitIntake(
  tenantId: string,
  submission: IntakeSubmission,
): Promise<{ participant_user_id: string; intake_id: string }> {
  const { data } = await apiClient.post<{ participant_user_id: string; intake_id: string }>(
    `${base(tenantId)}/intake`,
    submission,
  );
  return data;
}

export async function listParticipantIntakes(
  tenantId: string,
  participantUserId: string,
): Promise<ParticipantIntake[]> {
  const { data } = await apiClient.get<ParticipantIntake[]>(
    `${base(tenantId)}/participants/${participantUserId}/intakes`,
  );
  return data;
}

/** The signature / badge photo as an object URL (they need the auth header,
 *  so an <img src> to the API wouldn't work). Revoke it when done. */
export async function fetchIntakeImage(
  tenantId: string,
  intakeId: string,
  which: 'signature' | 'badge-photo',
): Promise<string> {
  const path = which === 'signature' ? 'signature.png' : 'badge-photo';
  const { data } = await apiClient.get<Blob>(`${base(tenantId)}/intakes/${intakeId}/${path}`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(data);
}

/** CSV of every response to a survey (all versions). */
export async function downloadSurveyResponses(tenantId: string, familyId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `${base(tenantId)}/intake/forms/${familyId}/responses.csv`,
    { responseType: 'blob' },
  );
  return data;
}
